import type {JSX, ReactNode} from 'react'
import {useEffect} from 'react'
import {useFormContext} from 'react-hook-form'
import {clsx} from 'clsx'
import {logError} from '@/services/logger/client'
import {ErrorMessage} from '../form'

interface DateFieldProps {
	type?: string
	label: ReactNode
	name: string
	className?: string
	isOptional?: boolean
	maxLength?: number
	requiredErrorMsg?: string
	invalidErrorMsg?: string
	defaultValue?: string
	minDate?: string
	maxDate?: string
	onChange?: (value: string) => void
	disabled?: boolean
}

const SVGPath = ({
	d,
	transform,
	id,
	dataName,
}: {
	d: string
	transform?: string
	id: string
	dataName?: string
}): JSX.Element => {
	return (
		<path
			d={d}
			data-name={dataName}
			fill="none"
			id={id}
			stroke="#000"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.5"
			transform={transform}
		/>
	)
}

const SVGRect = ({
	id,
	dataName,
	transform,
}: {
	id: string
	dataName: string
	transform: string
}): JSX.Element => {
	return (
		<rect
			data-name={dataName}
			fill="#000"
			height="3"
			id={id}
			transform={transform}
			width="3"
		/>
	)
}

export const DateField = ({
	defaultValue = '',
	minDate = '',
	maxDate = '',
	className = '',
	label,
	name,
	isOptional = false,
	requiredErrorMsg = '',
	invalidErrorMsg = '',
	maxLength = 100,
	onChange,
	disabled = false,
}: DateFieldProps): JSX.Element => {
	const requiredMsg = requiredErrorMsg || `${String(label)} is required`
	const invalidMsg =
		invalidErrorMsg || `Please enter a valid ${String(label)}`
	const {register, trigger, formState, clearErrors} = useFormContext()
	useEffect(() => {
		let ignore = false
		async function validation(): Promise<void> {
			if (defaultValue && !ignore) {
				await trigger(name)
			}
		}

		validation().catch((error) => {
			logError(`DateField Validation failed:${error}`)
		})

		return () => {
			ignore = true
		}
	}, [defaultValue, name, trigger])

	useEffect(() => {
		clearErrors()
	}, [minDate, maxDate, clearErrors])

	const {onChange: libOnChange, ...registerProps} = register(name, {
		required: !isOptional,
		min: minDate,
		max: maxDate,
	})

	return (
		<div className={clsx('relative mb-4', className)}>
			<label className="w-full text-xs text-pet-primary" htmlFor={name}>
				<span>
					{label}
					{!isOptional && <span className="text-red-600">*</span>}
				</span>
				<div className="relative mt-2">
					<input
						{...registerProps}
						className={clsx(
							formState.errors[name] ? 'invalid' : '',
							'text-sky-900 rounded-md !bg-white py-3 text-sm font-normal text-pet-primary'
						)}
						defaultValue={defaultValue}
						disabled={disabled}
						id={name}
						max={maxDate}
						maxLength={maxLength}
						min={minDate}
						name={name}
						onChange={(e) => {
							libOnChange(e).catch((error) => {
								logError(
									`DateField Error in libOnChange: ${error}`
								)
							})
							onChange?.(e.target.value)
						}}
						type="date"
					/>
					<svg
						className="mr-[10px] mt-[10px]"
						height="28.09"
						viewBox="0 0 25.204 28.09"
						width="25.204"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g
							data-name="Group 947"
							id="Group_947"
							transform="translate(0.75 0.75)"
						>
							<g
								data-name="Icon feather-calendar"
								id="Icon_feather-calendar"
								transform="translate(0 0)"
							>
								<SVGPath
									d="M7.134,6H25.57A2.654,2.654,0,0,1,28.2,8.674V27.39a2.654,2.654,0,0,1-2.634,2.674H7.134A2.654,2.654,0,0,1,4.5,27.39V8.674A2.654,2.654,0,0,1,7.134,6Z"
									data-name="Path 335"
									id="Path_335"
									transform="translate(-4.5 -3.473)"
								/>
								<SVGPath
									d="M24,3V8.054"
									dataName="Path 336"
									id="Path_336"
									transform="translate(-6.88 -3)"
								/>
								<SVGPath
									d="M12,3V8.054"
									dataName="Path 337"
									id="Path_337"
									transform="translate(-5.416 -3)"
								/>
								<SVGPath
									d="M4.5,15H28.2"
									dataName="Path 338"
									id="Path_338"
									transform="translate(-4.5 -4.364)"
								/>
							</g>
							<SVGRect
								dataName="Rectangle 869"
								id="Rectangle_869"
								transform="translate(7.837 12.769)"
							/>
							<SVGRect
								dataName="Rectangle 870"
								id="Rectangle_870"
								transform="translate(12.837 12.769)"
							/>
							<SVGRect
								dataName="Rectangle 871"
								id="Rectangle_871"
								transform="translate(17.837 12.769)"
							/>
							<SVGRect
								dataName="Rectangle 873"
								id="Rectangle_873"
								transform="translate(7.837 16.769)"
							/>
							<SVGRect
								dataName="Rectangle 874"
								id="Rectangle_874"
								transform="translate(12.837 16.769)"
							/>
							<SVGRect
								dataName="Rectangle 875"
								id="Rectangle_875"
								transform="translate(17.837 16.769)"
							/>
							<SVGRect
								dataName="Rectangle 876"
								id="Rectangle_876"
								transform="translate(2.837 16.769)"
							/>
							<SVGRect
								dataName="Rectangle 877"
								id="Rectangle_877"
								transform="translate(7.837 20.769)"
							/>
							<SVGRect
								dataName="Rectangle 878"
								id="Rectangle_878"
								transform="translate(12.837 20.769)"
							/>
							<SVGRect
								dataName="Rectangle 880"
								id="Rectangle_880"
								transform="translate(2.837 20.769)"
							/>
						</g>
					</svg>
					{formState.errors[name] ? (
						<ErrorMessage
							message={
								formState.errors[name]?.type === 'required'
									? requiredMsg
									: invalidMsg
							}
						/>
					) : null}
				</div>
			</label>
		</div>
	)
}
